import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import GroupRounded from '@mui/icons-material/GroupRounded';
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import WorkspacePremiumRounded from '@mui/icons-material/WorkspacePremiumRounded';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { Field, IconBadge, Panel, StatGrid, StatTile } from '../../components/ui';
import { businessDate, count, date, dateTime, plural, relativeTime } from '../../lib/format';
import { displayStatus, STATUS_LABELS, type TenantDetail } from './api';
import { FieldGrid } from './parts';

export function SupportAccessValue({ tenant }: { tenant: TenantDetail }) {
  const s = tenant.supportAccess;
  if (!s) return <>Unknown on this server</>;
  if (!s.active) return <>Not granted</>;
  return <>Active{s.expiresAt ? ` until ${dateTime(s.expiresAt)}` : ''}</>;
}

export function OverviewTab({ tenant }: { tenant: TenantDetail }) {
  const sub = tenant.subscription;
  const status = displayStatus(tenant);
  const tone =
    status === 'ACTIVE'
      ? 'success'
      : status === 'EXPIRING_SOON' || status === 'GRACE'
        ? 'warning'
        : status === 'NO_SUBSCRIPTION' || status === 'CLOSED'
          ? 'neutral'
          : 'error';
  return (
    <>
      <StatGrid>
        <StatTile
          label="Subscription"
          value={STATUS_LABELS[status]}
          hint={
            sub
              ? sub.locked
                ? 'Posting is blocked'
                : `${plural(sub.daysUntilLock, 'day')} until lock`
              : undefined
          }
          icon={<WorkspacePremiumRounded />}
          tone={tone}
        />
        <StatTile
          label="Paid through"
          value={sub ? businessDate(sub.paidThroughDate) : '—'}
          hint={sub ? `${plural(sub.daysRemaining, 'day')} remaining` : undefined}
          icon={<EventAvailableRounded />}
          tone="primary"
        />
        <StatTile
          label="Active users"
          value={count(tenant.activeUsers)}
          hint={plural(tenant.owners.length, 'owner')}
          icon={<GroupRounded />}
          tone="info"
        />
        <StatTile
          label="Support access"
          value={tenant.supportAccess?.active ? 'Granted' : 'Off'}
          hint={
            tenant.supportAccess?.active && tenant.supportAccess.expiresAt
              ? `Ends ${relativeTime(tenant.supportAccess.expiresAt)}`
              : 'Granted by the shop'
          }
          icon={<SupportAgentRounded />}
          tone={tenant.supportAccess?.active ? 'success' : 'neutral'}
        />
      </StatGrid>

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' },
          alignItems: 'start',
        }}
      >
        <Panel title="Owners" subtitle="Shop users with the Owner role">
          {tenant.owners.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No owner accounts found.
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />} spacing={1.5}>
              {tenant.owners.map((o) => (
                <Stack key={o.id} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                  <IconBadge icon={<PersonOutlineRounded />} size={36} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 650 }}>
                      {o.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {[o.email, o.phone].filter(Boolean).join(' · ') || 'No contact details'}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textAlign: 'right', flexShrink: 0 }}
                  >
                    {o.lastLoginAt ? `Seen ${relativeTime(o.lastLoginAt)}` : 'Never signed in'}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Panel>

        <Panel title="Shop details">
          <FieldGrid min={160}>
            <Field label="Phone">{tenant.contact?.phone ?? '—'}</Field>
            <Field label="Email">{tenant.contact?.email ?? '—'}</Field>
            <Field label="Time zone">{tenant.contact?.timeZone ?? '—'}</Field>
            <Field label="Currency">{tenant.contact?.currencyCode ?? '—'}</Field>
            <Field label="Created">{date(tenant.createdAt)}</Field>
            <Field label="Plan">{sub?.plan?.name ?? 'No plan'}</Field>
            <Field label="Support access">
              <SupportAccessValue tenant={tenant} />
            </Field>
            <Field label="Shop ID">
              <Box component="span" className="tabular" sx={{ fontSize: 12 }}>
                {tenant.id}
              </Box>
            </Field>
          </FieldGrid>
          {tenant.contact?.address && (
            <Box sx={{ mt: 2.25 }}>
              <Field label="Address">{tenant.contact.address}</Field>
            </Box>
          )}
        </Panel>
      </Box>
    </>
  );
}
